// frontend/src/components/stats/VoiceLogPanel.jsx
//
// 2026-10-07 — the voice → stats troubleshooting log for one game. Every
// step a piece of narration goes through, in order: engine session opened,
// each chunk received from the phone and forwarded to the engine (size,
// time, error), each engine callback and what Game Day did with it, each
// event heard and who it was tagged on (or why it wasn't), the official
// pass, WhatsApp fan-out, goals added by hand. The summary at the top says
// where narration is being lost; "Problems only" filters to warnings and
// errors. Coach / manager. Backend: GET /recordings/:gameId/voice-log.
import { useEffect, useMemo, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import api from '../../services/api.js';

const mmss = (ms) => {
  if (ms == null) return '';
  const s = Math.max(0, Math.round(Number(ms) / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};
const STAGES = ['session', 'chunk', 'webhook', 'event', 'manual', 'batch', 'notify', 'stop'];
const STAGE_LABEL = { session: 'Session', chunk: 'Audio', webhook: 'Engine', event: 'Heard', manual: 'By hand', batch: 'Official', notify: 'WhatsApp', stop: 'Stop' };
const COLOR = { ok: 'var(--text-3)', warn: 'var(--puck, #ff8a00)', error: 'var(--fire, #ff3b3b)' };

export default function VoiceLogPanel({ teamId, gameId }) {
  const [log, setLog] = useState(null);
  const [err, setErr] = useState(null);
  const [onlyProblems, setOnlyProblems] = useState(false);
  const [stages, setStages] = useState(() => new Set(STAGES.filter(s => s !== 'chunk')));
  const [open, setOpen] = useState(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let c = false;
    api.get(`/teams/${teamId}/recordings/${gameId}/voice-log`)
      .then(({ data }) => { if (!c) { setLog(data); setErr(null); } })
      .catch(e => { if (!c) setErr(e.response?.data?.error || 'Could not load the voice log'); });
    return () => { c = true; };
  }, [teamId, gameId, tick]);
  // live game: refresh every 15 s
  useEffect(() => {
    const live = (log?.recordings || []).some(r => r.status === 'recording');
    if (!live) return undefined;
    const id = setInterval(() => setTick(t => t + 1), 15000);
    return () => clearInterval(id);
  }, [log]);

  const rows = useMemo(() => (log?.entries || []).filter(e =>
    stages.has(e.stage) && (!onlyProblems || e.status !== 'ok')), [log, stages, onlyProblems]);

  if (err) return <div className="px-3 py-3 text-xs" style={{ color: 'var(--fire)' }}>{err}</div>;
  if (!log) return <div className="px-3 py-3 text-xs text-secondary">Loading the voice log…</div>;
  const s = log.summary || {};
  const Stat = ({ k, v, bad }) => (
    <div className="rounded-lg px-2 py-1" style={{ background: 'var(--surface-1)', border: `1px solid ${bad ? 'rgba(255,59,59,0.5)' : 'var(--border)'}` }}>
      <div style={{ fontSize: 16, fontWeight: 800, fontVariantNumeric: 'tabular-nums', color: bad ? 'var(--fire)' : 'var(--text)' }}>{v}</div>
      <div className="text-xs text-secondary">{k}</div>
    </div>
  );
  const lost = Math.max(0, (s.chunksReceived || 0) - (s.chunksForwarded || 0));

  return (
    <div className="px-3 py-3" data-voice-log>
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-bold" style={{ color: 'var(--text-2)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Voice AI log</div>
        <button type="button" onClick={() => setTick(t => t + 1)} className="flex items-center gap-1 text-xs"
          style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 8, padding: '2px 8px', color: 'var(--text-2)', cursor: 'pointer' }}>
          <RefreshCw size={11} /> Refresh
        </button>
      </div>

      {(log.recordings || []).length === 0 && <div className="text-xs text-secondary mb-2">No recording for this game yet.</div>}
      {(log.recordings || []).map(r => (
        <div key={r.id} className="text-xs mb-1" style={{ color: r.engine_error ? 'var(--fire)' : 'var(--text-2)' }}>
          Recording {new Date(r.started_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}: {r.status}, {r.chunk_count} chunks ({Math.round((r.duration_ms || 0) / 60000)} min),
          engine {r.engine_status || 'off'}{r.engine_mode ? ` · ${r.engine_mode} mode` : ''}{r.gap_count ? ` · ${r.gap_count} gaps` : ''}{r.engine_error ? ` · ${r.engine_error}` : ''}
        </div>
      ))}

      <div className="mt-2 mb-3" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 6 }}>
        <Stat k="audio chunks received" v={s.chunksReceived || 0} />
        <Stat k="sent to the engine" v={s.chunksForwarded || 0} bad={lost > 0 || s.chunkErrors > 0} />
        <Stat k="engine callbacks" v={s.webhooks || 0} bad={(s.chunksForwarded || 0) > 3 && !s.webhooks} />
        <Stat k="goals heard live" v={s.goalsHeard || 0} />
        <Stat k="goals in official pass" v={s.goalsHeardOfficial || 0} />
        <Stat k="goals heard, scorer not tagged" v={s.goalsUnattributed || 0} bad={s.goalsUnattributed > 0} />
        <Stat k="goals by score button" v={s.manualGoals || 0} />
        <Stat k="errors / warnings" v={`${s.errors || 0} / ${s.warnings || 0}`} bad={s.errors > 0} />
      </div>
      {lost > 0 && <div className="text-xs mb-2" style={{ color: 'var(--fire)' }}>⚠ {lost} chunk{lost === 1 ? '' : 's'} reached Game Day but never reached the voice AI — anything said in them was not heard.</div>}
      {(s.chunksForwarded || 0) > 3 && !s.webhooks && <div className="text-xs mb-2" style={{ color: 'var(--fire)' }}>⚠ Audio is reaching the voice AI but nothing is coming back — check the engine (hockeystats container) and its webhook secret.</div>}

      <div className="flex items-center gap-2 flex-wrap mb-2 text-xs">
        {STAGES.map(st => (
          <label key={st} className="flex items-center gap-1" style={{ cursor: 'pointer' }}>
            <input type="checkbox" checked={stages.has(st)} onChange={() => setStages(p => { const n = new Set(p); n.has(st) ? n.delete(st) : n.add(st); return n; })} />
            {STAGE_LABEL[st]}
          </label>
        ))}
        <label className="flex items-center gap-1 ml-auto" style={{ cursor: 'pointer', fontWeight: 700 }}>
          <input type="checkbox" checked={onlyProblems} onChange={e => setOnlyProblems(e.target.checked)} /> Problems only
        </label>
      </div>

      <div style={{ maxHeight: 460, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface-1)' }}>
        {rows.length === 0 && <div className="text-xs text-secondary p-2">Nothing to show with these filters.</div>}
        {rows.map(e => (
          <div key={e.id} onClick={() => setOpen(o => o === e.id ? null : e.id)} className="px-2 py-1 text-xs"
            style={{ borderBottom: '1px solid var(--border)', cursor: e.detail ? 'pointer' : 'default' }}>
            <div className="flex items-start gap-2">
              <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-3)', whiteSpace: 'nowrap' }}>
                {new Date(e.created_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', second: '2-digit' })}
              </span>
              <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-3)', minWidth: 36 }}>{mmss(e.tape_ms)}</span>
              <span style={{ minWidth: 58, fontWeight: 700, color: COLOR[e.status] }}>{STAGE_LABEL[e.stage] || e.stage}</span>
              <span style={{ color: e.status === 'ok' ? 'var(--text)' : COLOR[e.status] }}>{e.message}</span>
            </div>
            {e.detail?.said && <div style={{ marginLeft: 108, color: 'var(--text-3)', fontStyle: 'italic' }}>“{e.detail.said}”{e.detail.conf != null ? ` · confidence ${Math.round(e.detail.conf * 100)}%` : ''}</div>}
            {open === e.id && e.detail && (
              <pre style={{ marginLeft: 108, fontSize: 10.5, whiteSpace: 'pre-wrap', color: 'var(--text-2)', background: 'var(--surface-2)', padding: 6, borderRadius: 6 }}>{JSON.stringify(e.detail, null, 2)}</pre>
            )}
          </div>
        ))}
      </div>
      <div className="text-xs text-secondary mt-1">Tap a line for the raw details. Audio lines are hidden by default — tick “Audio” to see every chunk.</div>
    </div>
  );
}
